import React, { useState, useRef, useEffect } from 'react'
import Input from 'components/input'
import { MenuProps } from 'components/menu'
import FloatingMenu from 'components/menu/floating'
import { cancelTimeout, requestTimeout, TimeoutID } from 'helpers/timer'
import { updateColumn } from 'components/spreadsheet/contexts/data/actions'
import { useColumnRename } from 'components/spreadsheet/hooks/useColumnRename'
import { getApiErrorMessage } from 'helpers/apiError'
import { useApplicationStore } from 'hooks/application'
import Checkbox from 'components/checkbox'
import ColumnDescriptionField from 'components/spreadsheet/components/column/description-field'
import {
  ALL_TYPES,
  SELECT_COLUMN_TYPES,
  BLOCK_SELECT_OPTION_TYPES,
  BLOCK_GROUP_BY,
  COLUMN_TYPE_NAMES,
  DATE_COLUMN_TYPES,
  NUMBER_COLUMN_TYPES,
  VOTE_COLUMN_TYPES,
  ATTACHMENT_COLUMN_TYPES,
  DEFAULT_KIND_OPTIONS
} from 'components/spreadsheet/constants/const'
import { IColumnTypes } from 'components/spreadsheet/types'
import {
  ITableJoin,
  ITableViewWithColumns,
  SelectKindOptions,
  ITableViewColumn,
  SelectDependencyColumn,
  EditorContent
} from 'types'
import { Delete, Join, ErrorOutline, ChevronDown, Expand, Cross } from 'components/icons'
import Tooltip from 'components/tooltip'
import { Link, generatePath } from 'react-router-dom'
import { useProject } from 'hooks/useProject'
import api from 'helpers/api'
import IconSelector from 'components/spreadsheet/components/cell/components/icon'
import ColumnKindModal from 'components/spreadsheet/components/modal/views/columnkind'
import { isEditorEmpty, getDefaultValueError } from 'components/spreadsheet/helpers/functions'
import { toUTCString, formatDate, formatDateTime } from 'components/spreadsheet/helpers/format'
import Button from 'components/button'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { getFilterValues } from 'components/spreadsheet/helpers/filtering'
import Select from 'components/select'
import Field from 'components/field'
import Collapse, { CollapseGroup } from 'components/collapse'
import InfoHint from 'components/info-hint'
import SegmentedControl from 'components/segmented-control'
import ColumnManualOptions from 'components/spreadsheet/components/column/manual-options'
import ColumnValidationFields, {
  ColumnValidationValue
} from 'components/spreadsheet/components/column/validation-fields'
import ColumnFormattingFields, {
  ColumnTypeFormattingFields,
  ColumnFormattingValue
} from 'components/spreadsheet/components/column/formatting-fields'
import SelectSourceFields, {
  LIVE_VALUES_HELP,
  AUTOPOPULATE_HELP
} from 'components/spreadsheet/components/column/select-source-fields'
import {
  SELECT_OPTIONS_MODES,
  SelectOptionsMode
} from 'components/spreadsheet/components/column-type-picker/useColumnTypePicker'
import Formula, { DEFAULT_FORMULA } from './components/formula'
import Script from './components/script'
import { TABLE } from 'routes'
import Spinner from 'components/spinner'
import { tv } from 'tailwind-variants'

// Structured / widget kinds whose value is built through a modal (files, votes, link
// lists) rather than typed in — a user-entered default is meaningless for them and the
// backend rejects almost any string, so we don't show the Default Value field at all.
const DEFAULT_VALUE_UNSUPPORTED_KINDS: IColumnTypes[] = [...VOTE_COLUMN_TYPES, ...ATTACHMENT_COLUMN_TYPES, 'multilink']

const EDIT_COLUMN_MENU_WIDTH = 340
// Formula panel
const SUB_PANEL_WIDTH = 560

const panel = tv({
  base: 'flex flex-col',
  variants: {
    view: {
      settings: 'max-h-[80vh]',
      subPanel: 'h-[460px] max-h-[80vh]',
      fullScreen: 'h-full'
    }
  }
})

const panelView = (subPanel: SubPanel | null, fullScreen: boolean): 'settings' | 'subPanel' | 'fullScreen' => {
  if (!subPanel) return 'settings'
  return fullScreen ? 'fullScreen' : 'subPanel'
}

const subPanelRow = tv({
  base: 'flex items-center cursor-pointer border-t border-divider py-2.5 px-2.5',
  variants: {
    active: {
      true: 'bg-light-blue',
      false: 'bg-surface-sunken'
    }
  }
})

// A field that is typed into rather than clicked waits for a pause in typing before it saves, so a
// value isn't sent per keystroke. Everything else (checkboxes, dropdowns) saves on change.
const SAVE_DEBOUNCE_MS = 1000

// Everything the shared validation/formatting components can patch. Each key is also an `updateColumn`
// field name, which is what lets a patch be routed straight to the API without a translation table.
type ColumnConfigValue = ColumnValidationValue & ColumnFormattingValue
type ColumnConfigKey = keyof ColumnConfigValue

// Every field of the panel that saves after a pause in typing rather than on change.
type PendingSaveKey = ColumnConfigKey | 'manualOptions' | 'description' | 'defaultValue'

const DEBOUNCED_CONFIG_FIELDS: ColumnConfigKey[] = [
  'stringValidation',
  'validationMessage',
  'dateFormat',
  'decimalPlaces',
  'exportWidth',
  // The colour pickers carry a hex input, so they debounce for the same reason the text fields do.
  'headerBackgroundColor',
  'headerTextColor'
]

// Which editor the menu has drilled into, if any.
type SubPanel = 'formula' | 'script'

const SubPanelRow: React.FC<{ title: string; state: 'On' | 'Off'; active: boolean; onOpen: () => void }> = ({
  title,
  state,
  active,
  onOpen
}) => (
  <div
    role="button"
    tabIndex={0}
    aria-expanded={active}
    className={subPanelRow({ active })}
    onClick={onOpen}
    onKeyDown={(event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      event.preventDefault()
      onOpen()
    }}
  >
    <span className="font-bold">{title}</span>
    <span className="ml-2 text-muted text-[90%]">{state}</span>
    <span className="ml-auto flex text-muted -rotate-90">
      <ChevronDown />
    </span>
  </div>
)

// The column's saved validation + formatting settings, in the shape the shared field components expect.
const columnConfigValue = (column?: ITableViewColumn): ColumnConfigValue => ({
  stringValidation: column?.stringValidation ?? null,
  validationMessage: column?.validationMessage ?? null,
  validationNoDuplicates: column?.validationNoDuplicates ?? false,
  validationNoBlanks: column?.validationNoBlanks ?? false,
  hardValidation: column?.hardValidation ?? false,
  blockInvalidSelectValues: column?.blockInvalidSelectValues ?? false,
  displayValidationError: column?.displayValidationError ?? false,
  thousandSeparator: column?.thousandSeparator ?? false,
  decimalPlaces: column?.decimalPlaces ?? 2,
  dateFormat: column?.dateFormat ?? null,
  displayLink: column?.displayLink ?? false,
  headerBackgroundColor: column?.headerBackgroundColor ?? '#ffffff',
  headerTextColor: column?.headerTextColor ?? '#333333',
  exportWidth: column?.exportWidth ?? null
})

// Why a text-validation pattern won't compile, or undefined when it's valid. Module-scoped because the
// React Compiler can't yet handle a conditional expression inside a try/catch.
const getRegexError = (pattern: string | null | undefined): string | undefined => {
  try {
    new RegExp(pattern ?? '')
    return undefined
  } catch (error) {
    return error instanceof SyntaxError ? error.message : String(error)
  }
}

// Stored ISO8601 default -> the value an <input type="date|datetime-local"> expects.
const formatDefaultDateForInput = (value: string | null | undefined, kind: IColumnTypes): string => {
  if (!value) return ''
  return kind === 'datetime' ? formatDateTime(value + 'Z', 'YYYY-MM-DDTHH:mm:ss') : formatDate(value, 'YYYY-MM-DD')
}

// Object spread only fills in *missing* keys, so an explicit `null` from the API
// (e.g. a select column whose stored manual_options is NULL) would overwrite the
// array defaults and crash consumers that call `.length`. Coerce those nulls back
// to their empty-list defaults here so the rest of the component can assume arrays.
const mergeKindOptions = (kindOptions?: SelectKindOptions): SelectKindOptions => {
  const merged = {
    ...DEFAULT_KIND_OPTIONS,
    ...(kindOptions ?? {}),
    tableOptions: {
      ...DEFAULT_KIND_OPTIONS.tableOptions,
      ...(kindOptions?.tableOptions ?? {})
    }
  }
  return {
    ...merged,
    manualOptions: merged.manualOptions ?? [],
    tableOptions: {
      ...merged.tableOptions,
      cachedOptions: merged.tableOptions.cachedOptions ?? [],
      dependencies: merged.tableOptions.dependencies ?? []
    }
  }
}

const EditColumnMenu: React.FC<MenuProps> = ({ id, menuState, setMenuState, width }) => {
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const { spreadsheetData, setSpreadsheetData } = useDataContext()

  const column = spreadsheetData.viewDetails.columns.find((col) => col.publicId === menuState.columnId)

  const { publicId, description, kind, script, scriptEnabled, formula, formulaEnabled, isJoined } = column || {}
  const [formulaHasChanges, setFormulaHasChanges] = useState<boolean>(false)
  const [selectedKind, setSelectedKind] = useState<IColumnTypes>()
  const [processing, setProcessing] = useState<boolean>(false)
  const [changeColumnKindModalState, setChangeColumnKindModalState] = useState<boolean>(false)
  const [showDependencyColumn, setShowDependencyColumn] = useState<boolean>(false)
  const [showDependencyJoinColumn, setShowDependencyJoinColumn] = useState<boolean>(false)
  const [canAddDependency, setCanAddDependency] = useState<boolean>(false)
  const [dependencyColumn, setDependencyColumn] = useState<SelectDependencyColumn>({
    columnId: '',
    columnJoinId: ''
  })
  const [regexError, setRegexError] = useState<string>()
  // Which editor the menu has drilled into. Null while the settings fill the whole panel.
  const [subPanel, setSubPanel] = useState<SubPanel | null>(menuState.columnPanel ?? null)
  const [subPanelFullScreen, setSubPanelFullScreen] = useState<boolean>(false)

  // Select properties
  const { project } = useProject()
  const [selectedSourceView, setSelectedSourceView] = useState<ITableViewWithColumns>()
  const [currentKindOptions, setCurrentKindOptions] = useState<SelectKindOptions>(mergeKindOptions(column?.kindOptions))
  const [optionsMode, setOptionsMode] = useState<SelectOptionsMode>(
    column?.kindOptions?.tableOptions?.tableId ? 'table' : 'manual'
  )
  const [columnJoin, setColumnJoin] = useState<ITableJoin>()
  const [columnJoinLoading, setColumnJoinLoading] = useState<boolean>(false)
  const sourceColumnName = columnJoin?.dataColumns.find((dc) => dc.targetColumnId === publicId)?.sourceColumnName

  // Loaders
  const [sourceRestricted, setSourceRestricted] = useState<boolean>(false)

  // Edits the user has made that the API hasn't echoed back yet. Rendering the saved column merged with
  // these keeps every control responsive even while its save is still debouncing, and a key drops out
  // again the moment the server confirms it — so a change made anywhere else still reaches the panel.
  const [pendingConfigPatch, setPendingConfigPatch] = useState<Partial<ColumnConfigValue>>({})
  const configValue: ColumnConfigValue = { ...columnConfigValue(column), ...pendingConfigPatch }

  // Debounced saves that haven't fired yet, keyed by field so a second edit to the same field replaces
  // the first, and so closing the panel can flush them all rather than dropping them. Every typed field
  // debounces through here — the description, default value and a select column's manual option list
  // as well as the validation/formatting settings.
  const pendingSaves = useRef<Map<PendingSaveKey, { timeout: TimeoutID; save: () => void }>>(new Map())

  // Drop a save still waiting for this field — it's about to be replaced, or sent straight away.
  const cancelPendingSave = (key: PendingSaveKey): void => {
    const existing = pendingSaves.current.get(key)
    if (existing) cancelTimeout(existing.timeout)
    pendingSaves.current.delete(key)
  }

  // Queue a save for when typing pauses, replacing one still waiting for the same field.
  const debounceSave = (key: PendingSaveKey, save: () => void): void => {
    cancelPendingSave(key)
    pendingSaves.current.set(key, {
      save,
      timeout: requestTimeout(() => {
        pendingSaves.current.delete(key)
        save()
      }, SAVE_DEBOUNCE_MS)
    })
  }

  const flushPendingSaves = (): void => {
    pendingSaves.current.forEach(({ timeout, save }) => {
      cancelTimeout(timeout)
      save()
    })
    pendingSaves.current.clear()
  }

  useEffect(
    () => () => {
      pendingSaves.current.forEach(({ timeout }) => cancelTimeout(timeout))
      pendingSaves.current.clear()
    },
    []
  )

  useEffect(() => {
    if (isJoined && menuState.open) {
      setColumnJoinLoading(true)
      api
        .getTableJoins(spreadsheetData.tableDetails.publicId)
        .then((response) => {
          const join = response.data.find((j: ITableJoin) => j.dataColumns.some((dc) => dc.targetColumnId === publicId))
          setColumnJoin(join)
        })
        .catch(() => setColumnJoin(undefined))
        .finally(() => setColumnJoinLoading(false))
    } else {
      setColumnJoin(undefined)
      setColumnJoinLoading(false)
    }
  }, [isJoined, menuState.open, menuState.columnId])

  useEffect(() => {
    if (currentKindOptions.tableOptions.dependencies.length > 0) setShowDependencyColumn(true)
  }, [currentKindOptions])

  // The debounced option-list save reads the column's other kindOptions when it finally fires, not when
  // it was scheduled, so a toggle flipped mid-edit isn't written back stale.
  const kindOptionsRef = useRef(currentKindOptions)
  useEffect(() => {
    kindOptionsRef.current = currentKindOptions
  })

  useEffect(() => {
    const columnFound = spreadsheetData.viewDetails.columns.find((col) => col.publicId === menuState.columnId)
    if (kind && SELECT_COLUMN_TYPES.includes(kind)) {
      setCurrentKindOptions(mergeKindOptions(columnFound?.kindOptions))
    } else {
      setCurrentKindOptions(DEFAULT_KIND_OPTIONS)
    }
  }, [
    kind,
    menuState.columnNumber !== undefined &&
      spreadsheetData.viewDetails.columns[menuState.columnNumber] &&
      spreadsheetData.viewDetails.columns[menuState.columnNumber].kindOptions
  ])

  // Everything held for one column has to be dropped when the panel moves to another: the drafts the
  // server hasn't confirmed, the drilled-into editor, and the select-source state loaded for it.
  useEffect(() => {
    setPendingConfigPatch({})
    setSubPanel(menuState.columnPanel ?? null)
    setSubPanelFullScreen(false)
    setRegexError(undefined)
    setSelectedSourceView(undefined)
    setDependencyColumn({ columnId: '', columnJoinId: '' })
    setCanAddDependency(false)
    setShowDependencyJoinColumn(false)
    setShowDependencyColumn(false)
    setOptionsMode(column?.kindOptions?.tableOptions?.tableId ? 'table' : 'manual')
  }, [menuState.columnId, menuState.columnPanel])

  // The distinct values the table already holds in this column. Walking every row is too expensive to
  // repeat per render, so it is recomputed only when the column or its rows change; which of them are
  // still unused is a cheap set difference below. Blanks are skipped — an empty option is never useful,
  // and the bulk-add this replaced used to create one.
  const [columnValues, setColumnValues] = useState<string[]>([])
  useEffect(() => {
    if (!column || !SELECT_COLUMN_TYPES.includes(column.kind)) {
      setColumnValues([])
      return
    }
    const distinct = new Set<string>()
    // With bitmap model, all rows (visible + filtered) are in spreadsheetData.rows
    getFilterValues(
      spreadsheetData.rows,
      column,
      column.publicId,
      spreadsheetData.columnIdToIndex,
      spreadsheetData.columnStore
    ).forEach((value: unknown) => {
      const text = value === null || value === undefined ? '' : String(value)
      if (text !== '') distinct.add(text)
    })
    setColumnValues([...distinct].sort((valueA, valueB) => valueA.localeCompare(valueB)))
  }, [menuState.columnId, column?.kind, spreadsheetData.rows])

  const handleUpdateColumnSuccess = () => {
    setSnackbarMessage({ status: 'success', message: 'The column was updated successfully!' })
    setProcessing(false)
  }

  const handleUpdateColumnError = (error: any) => {
    setProcessing(false)
    setSelectedKind(undefined)
    displayErrorMessage(error)
  }

  // The name is the one field not saved on a debounce while typing: it commits on Enter, blur, or
  // panel close. See useColumnRename for the serialized save behaviour (issue #10385).
  const rename = useColumnRename({
    savedName: column?.name ?? '',
    save: (newName) => {
      setProcessing(true)
      return new Promise<void>((resolve, reject) => {
        if (!publicId) {
          reject(new Error('Column cannot be renamed'))
          return
        }
        updateColumn(
          project.publicId,
          'name',
          spreadsheetData,
          setSpreadsheetData,
          publicId,
          newName,
          () => resolve(),
          (error) => reject(error)
        )
      }).finally(() => setProcessing(false))
    },
    onSaved: handleUpdateColumnSuccess,
    getErrorMessage: (error) => getApiErrorMessage(error, 'The column name could not be saved.')
  })

  // Show the column's saved name whenever the panel opens or switches column. The panel's state
  // outlives the menu (Menu renders null when closed), so without this a draft would leak across.
  useEffect(() => {
    if (!menuState.open) return
    rename.reset(column?.name ?? '')
  }, [menuState.open, menuState.columnId])

  // Send one validation/formatting field to the API. The draft keeps showing `value` until the response
  // comes back, at which point the saved column is authoritative again — unless the user has since
  // changed the same field, in which case their newer draft stays.
  const saveConfigField = (key: ColumnConfigKey, value: ColumnConfigValue[ColumnConfigKey]): void => {
    if (!publicId) return
    const settle = (): void =>
      setPendingConfigPatch((pending) => {
        if (pending[key] !== value) return pending
        return Object.fromEntries(Object.entries(pending).filter(([field]) => field !== key))
      })
    setProcessing(true)
    updateColumn(
      project.publicId,
      key,
      spreadsheetData,
      setSpreadsheetData,
      publicId,
      value,
      () => {
        settle()
        handleUpdateColumnSuccess()
      },
      (error) => {
        settle()
        handleUpdateColumnError(error)
      }
    )
  }

  // Route a patch from the shared validation/formatting components to the column. Each component emits
  // one key at a time; the key is already an `updateColumn` field, so this only decides when to send it.
  const handleConfigChange = (patch: Partial<ColumnConfigValue>): void => {
    setPendingConfigPatch((pending) => ({ ...pending, ...patch }))
    ;(Object.keys(patch) as ColumnConfigKey[]).forEach((key) => {
      const value = patch[key] as ColumnConfigValue[ColumnConfigKey]

      // An unparseable regex is reported inline and never sent — the backend would reject it anyway.
      if (key === 'stringValidation') {
        const error = getRegexError(value as string | null)
        setRegexError(error)
        if (error) return
      }

      if (!DEBOUNCED_CONFIG_FIELDS.includes(key)) {
        cancelPendingSave(key)
        saveConfigField(key, value)
        return
      }

      debounceSave(key, () => saveConfigField(key, value))
    })
  }

  const handleScriptEnabledChange = (checked: boolean) => {
    if (isJoined || !publicId || checked === undefined) return
    updateColumn(
      project.publicId,
      'scriptEnabled',
      spreadsheetData,
      setSpreadsheetData,
      publicId,
      checked,
      handleUpdateColumnSuccess,
      handleUpdateColumnError
    )
  }

  const handleDescriptionChange = (description: EditorContent) => {
    if (!publicId) return
    debounceSave('description', () => {
      setProcessing(true)
      updateColumn(
        project.publicId,
        'description',
        spreadsheetData,
        setSpreadsheetData,
        publicId,
        isEditorEmpty(description) ? null : description,
        handleUpdateColumnSuccess,
        handleUpdateColumnError
      )
    })
  }

  const handleDefaultValueChange = (value: string | null) => {
    if (!publicId) return
    debounceSave('defaultValue', () => {
      setProcessing(true)
      updateColumn(
        project.publicId,
        'defaultValue',
        spreadsheetData,
        setSpreadsheetData,
        publicId,
        value || null,
        handleUpdateColumnSuccess,
        handleUpdateColumnError
      )
    })
  }

  const parseMultiselectDefault = (value: string | null | undefined): string[] => {
    if (!value) return []
    let parsed: unknown
    try {
      parsed = JSON.parse(value)
    } catch {
      return []
    }
    return Array.isArray(parsed) ? parsed : []
  }

  const [selectDefault, setSelectDefault] = useState<string | null>(column?.defaultValue ?? null)

  const [multiselectDefaults, setMultiselectDefaults] = useState<string[]>(
    parseMultiselectDefault(column?.defaultValue)
  )

  const handleSelectDefaultValueChange = (option: string) => {
    const newValue = option === selectDefault ? null : option
    setSelectDefault(newValue)
    handleDefaultValueChange(newValue)
  }

  const handleMultiselectDefaultValueChange = (option: string) => {
    setMultiselectDefaults((prev) => {
      const exists = prev.includes(option)
      const updated = exists ? prev.filter((v) => v !== option) : [...prev, option]
      const serialized = updated.length > 0 ? JSON.stringify(updated) : null
      handleDefaultValueChange(serialized)
      return updated
    })
  }

  const [checkboxDefault, setCheckboxDefault] = useState<string | null>(
    column?.defaultValue === 'true' || column?.defaultValue === 'false' ? column.defaultValue : null
  )

  const handleCheckboxDefaultValueChange = (option: string) => {
    const newValue = option === checkboxDefault ? null : option
    setCheckboxDefault(newValue)
    handleDefaultValueChange(newValue)
  }

  // Flag a stored default that no longer matches the column type (e.g. a "N/A" text
  // default left behind after switching the column to date). Such a default is ignored
  // by the backend on insert; this shows the user why. Type-specific inputs below stop
  // new mismatches from being entered, so this mainly catches stale values.
  const defaultValueError = kind ? getDefaultValueError(kind, column?.defaultValue) : null

  const handleOnKindChange = (selectedKind: string) => {
    const newKind = selectedKind as IColumnTypes
    const isGroupedColumn = !!spreadsheetData.userConfiguration.groupSettings.find(
      (group) => group.columnId === publicId
    )

    if (isGroupedColumn && BLOCK_GROUP_BY.includes(newKind)) {
      setSnackbarMessage({
        status: 'error',
        message: `This column groups the view, and ${COLUMN_TYPE_NAMES[newKind]} columns can't be used for grouping. Remove it from the grouping first.`
      })
      return
    }
    setSelectedKind(newKind)
    setChangeColumnKindModalState(true)
  }

  const handleOnTableChange = (tableId: string) => {
    setCurrentKindOptions({
      ...currentKindOptions,
      tableOptions: {
        cachedOptions: [],
        tableId: tableId,
        viewId: '',
        columnId: '',
        dependencies: [],
        liveValues: false
      }
    })
  }

  const handleOnTableViewChange = (viewId: string) => {
    setCurrentKindOptions({
      ...currentKindOptions,
      tableOptions: {
        ...currentKindOptions.tableOptions,
        viewId: viewId,
        columnId: '',
        dependencies: []
      }
    })
  }

  const handleOnTableViewColumnChange = (columnId: string) => {
    const foundColumn = selectedTableViewColumns?.find((column) => column.publicId === columnId)
    if (!foundColumn) return
    const columnKind = foundColumn.kind
    if (BLOCK_SELECT_OPTION_TYPES.includes(columnKind)) {
      setCurrentKindOptions({
        ...currentKindOptions,
        tableOptions: {
          ...currentKindOptions.tableOptions,
          columnId: '',
          dependencies: []
        }
      })
      setCanAddDependency(false)
      setDependencyColumn({ columnId: '', columnJoinId: '' })
      setSnackbarMessage({
        status: 'error',
        message: `${COLUMN_TYPE_NAMES[columnKind]} columns can't supply select options. Choose a different column.`
      })
      return
    }
    const newCurrentKindOptions = {
      ...currentKindOptions,
      tableOptions: { ...currentKindOptions.tableOptions, columnId: columnId }
    }
    setProcessing(true)
    setCurrentKindOptions(newCurrentKindOptions)
    handleUpdateKindOptions(newCurrentKindOptions, (error) => {
      setCurrentKindOptions({
        ...currentKindOptions,
        tableOptions: {
          ...currentKindOptions.tableOptions,
          columnId: '',
          dependencies: []
        }
      })
      handleUpdateColumnError(error)
    })
  }

  const handleOnColumnDependencyChange = (columnId: string) => {
    const foundColumn = spreadsheetData.viewDetails.columns.find((col) => col.publicId === columnId)
    if (foundColumn && BLOCK_SELECT_OPTION_TYPES.includes(foundColumn.kind)) {
      setDependencyColumn({ ...dependencyColumn, columnId: '', columnJoinId: '' })
      setShowDependencyJoinColumn(true)
      setCanAddDependency(false)
      setSnackbarMessage({
        status: 'error',
        message: `${COLUMN_TYPE_NAMES[foundColumn.kind]} columns can't be used as dependencies. Choose a different column.`
      })
      return
    }
    if (foundColumn && SELECT_COLUMN_TYPES.includes(foundColumn.kind)) {
      const needsJoin =
        foundColumn.kindOptions?.tableOptions?.tableId !== tableId ||
        foundColumn.kindOptions?.tableOptions?.viewId !== viewId
      setShowDependencyJoinColumn(needsJoin)
      setCanAddDependency(!needsJoin)
    } else {
      setShowDependencyJoinColumn(true)
      setCanAddDependency(false)
    }

    setDependencyColumn({ ...dependencyColumn, columnId: columnId })
  }

  const handleOnColumnDependencyJoinChange = (columnId: string) => {
    if (columnId === currentKindOptions.tableOptions.columnId) {
      setCanAddDependency(false)
      setSnackbarMessage({
        status: 'error',
        message:
          "This column already supplies the options, so it can't also be a dependency. Choose a different column."
      })
      return
    }
    const foundColumn =
      selectedTableViewColumns && selectedTableViewColumns.find((column) => column.publicId === columnId)
    if (foundColumn && BLOCK_SELECT_OPTION_TYPES.includes(foundColumn.kind)) {
      setCanAddDependency(false)
      setSnackbarMessage({
        status: 'error',
        message: `${COLUMN_TYPE_NAMES[foundColumn.kind]} columns can't be used as dependencies. Choose a different column.`
      })
      return
    }

    setDependencyColumn({ ...dependencyColumn, columnJoinId: columnId })
    setCanAddDependency(true)
  }

  const handleAddDependencyColumn = () => {
    if (!canAddDependency) return

    const newDependencies = currentKindOptions.tableOptions.dependencies
      ? [...currentKindOptions.tableOptions.dependencies]
      : []

    newDependencies.push(dependencyColumn)
    const newCurrentKindOptions = {
      ...currentKindOptions,
      tableOptions: {
        ...currentKindOptions.tableOptions,
        dependencies: newDependencies
      }
    }
    setCanAddDependency(false)
    setShowDependencyJoinColumn(false)
    handleUpdateKindOptions(newCurrentKindOptions, (error) => {
      handleUpdateColumnError(error ?? "The dependency couldn't be added. Try again.")
    })
    setDependencyColumn({ columnId: '', columnJoinId: '' })
  }

  const handleRemoveTableOptions = () => {
    if (currentKindOptions.tableOptions.tableId) {
      const newCurrentKindOptions = {
        ...currentKindOptions,
        tableOptions: {
          ...currentKindOptions.tableOptions,
          tableId: '',
          viewId: '',
          columnId: '',
          dependencies: []
        }
      }
      setCurrentKindOptions(newCurrentKindOptions)
      handleUpdateKindOptions(newCurrentKindOptions, handleUpdateColumnError)
    }
  }

  const handleRemoveTableDependency = (index: number) => {
    const newDependencies = [...currentKindOptions.tableOptions.dependencies]
    newDependencies.splice(index, 1)
    const newCurrentKindOptions = {
      ...currentKindOptions,
      tableOptions: {
        ...currentKindOptions.tableOptions,
        dependencies: newDependencies
      }
    }
    setCurrentKindOptions(newCurrentKindOptions)
    handleUpdateKindOptions(newCurrentKindOptions, handleUpdateColumnError)
    if (newDependencies.length === 0) {
      setShowDependencyColumn(false)
    }
  }

  const handleSetLiveValues = (liveValues: boolean) => {
    if (processing) return
    handleUpdateKindOptions(
      {
        ...currentKindOptions,
        tableOptions: {
          ...currentKindOptions.tableOptions,
          liveValues
        }
      },
      handleUpdateColumnError
    )
  }

  const handleSetAutopopulateValues = (autopopulate: boolean) => {
    if (processing) return
    handleUpdateKindOptions(
      {
        ...currentKindOptions,
        tableOptions: {
          ...currentKindOptions.tableOptions
        },
        autopopulate
      },
      handleUpdateColumnError
    )
  }

  const handleSetExpandOptionsViaCellEntry = (expandOptionsViaCellEntry: boolean) => {
    if (processing) return
    handleUpdateKindOptions(
      {
        ...currentKindOptions,
        expandOptionsViaCellEntry
      },
      handleUpdateColumnError
    )
  }

  // Apply a new manual list: shown at once, then persisted. A typed edit waits out the same pause the
  // other typed fields do so a save isn't sent per keystroke; adding, deleting, reordering and sorting
  // are single deliberate actions, so they save immediately.
  const commitManualOptions = (nextOptions: string[], debounce = false): void => {
    if (processing && !debounce) return
    setCurrentKindOptions((previous) => ({ ...previous, manualOptions: nextOptions }))

    const save = (): void => {
      // Inline editing makes a duplicate easy to type into existence; the list is only sent once it is
      // valid, and the draft stays on screen for the user to fix.
      if (new Set(nextOptions).size !== nextOptions.length) {
        setSnackbarMessage({
          status: 'error',
          message: 'Each option must be different. Change or remove the repeated option.'
        })
        return
      }
      handleUpdateKindOptions({ ...kindOptionsRef.current, manualOptions: nextOptions }, handleUpdateColumnError)
    }

    if (!debounce) {
      cancelPendingSave('manualOptions')
      save()
      return
    }
    debounceSave('manualOptions', save)
  }

  const handleAddManualOption = (value: string): void => {
    const trimmed = value.trim()
    if (!trimmed) return
    if (getOptions().includes(trimmed)) {
      setSnackbarMessage({ status: 'error', message: `'${trimmed}' is already an option.` })
      return
    }
    commitManualOptions([...manualOptions, trimmed])
  }

  const handleUpdateManualOption = (index: number, value: string): void =>
    commitManualOptions(
      manualOptions.map((option, position) => (position === index ? value : option)),
      true
    )

  const handleRemoveManualOption = (index: number): void =>
    commitManualOptions(manualOptions.filter((_, position) => position !== index))

  const handleMoveManualOption = (from: number, to: number): void => {
    if (from === to || from < 0 || to < 0 || from >= manualOptions.length || to >= manualOptions.length) return
    const next = [...manualOptions]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    commitManualOptions(next)
  }

  // Sorts A→Z, or Z→A when the list is already ascending — the same toggle the add flow offers.
  const handleSortManualOptions = (): void => {
    const ascending = [...manualOptions].sort((a, b) => a.localeCompare(b))
    const alreadyAscending = manualOptions.every((value, index) => value === ascending[index])
    commitManualOptions(alreadyAscending ? [...ascending].reverse() : ascending)
  }

  const handleUpdateKindOptions = (kindOptions: SelectKindOptions, onError: (error?: string) => void) => {
    setProcessing(true)
    const newKindOptions = JSON.parse(JSON.stringify(kindOptions))
    delete newKindOptions.publicId
    if (newKindOptions?.tableOptions) {
      delete newKindOptions.tableOptions.cachedOptions
    }
    if (publicId) {
      updateColumn(
        project.publicId,
        'kindOptions',
        spreadsheetData,
        setSpreadsheetData,
        publicId,
        newKindOptions,
        handleUpdateColumnSuccess,
        onError
      )
    }
  }

  const getOptions = () => {
    let allOptions: string[] = []
    if (currentKindOptions.manualOptions.length > 0) {
      allOptions = allOptions.concat(currentKindOptions.manualOptions)
    }
    if (
      Array.isArray(currentKindOptions.tableOptions.cachedOptions) &&
      currentKindOptions.tableOptions.cachedOptions.length > 0
    ) {
      allOptions = allOptions.concat(currentKindOptions.tableOptions.cachedOptions)
    }
    return allOptions
  }

  // Picking several values in a row debounces into one save, the same as a typed edit, so a burst of
  // clicks doesn't become a burst of requests.
  const handleAddColumnValues = (values: string[]): void => {
    const additions = values.filter((value) => !manualOptions.includes(value))
    if (additions.length === 0) return
    commitManualOptions([...manualOptions, ...additions], true)
  }

  const manualOptions = currentKindOptions.manualOptions
  const takenOptions = new Set(getOptions())
  const unusedColumnValues = columnValues.filter((value) => !takenOptions.has(value))
  const sourcedOptions = Array.isArray(currentKindOptions.tableOptions.cachedOptions)
    ? currentKindOptions.tableOptions.cachedOptions
    : []
  const tableId = currentKindOptions.tableOptions.tableId
  const viewId = currentKindOptions.tableOptions.viewId
  const viewColumnId = currentKindOptions.tableOptions.columnId
  const dependencies = currentKindOptions.tableOptions.dependencies || []
  // Names come from the source view as the user can read it, which also covers a table they can only
  // reach through a view-level role. A stale view from an earlier selection is ignored.
  const sourceView = selectedSourceView?.publicId === viewId ? selectedSourceView : undefined
  const selectedTableViewColumns = sourceView?.columns
  const selectedTableName = sourceView?.documentTable?.name
  const selectedViewName = sourceView?.name
  const disabled = isJoined || processing
  const isSynced = column?.isSynced
  const disableSelectConfiguration = isJoined || isSynced
  const isReadOnly = isJoined || isSynced
  const scriptsAllowed = !isReadOnly && scriptEnabled
  const isSelectColumn =
    (selectedKind && SELECT_COLUMN_TYPES.includes(selectedKind)) || (kind && SELECT_COLUMN_TYPES.includes(kind))
  const showSelectOptions = !disableSelectConfiguration && isSelectColumn

  // Leaving the formula editor with unsaved text is the one navigation worth interrupting; the guard is
  // the same one Menu applies to closing the whole panel.
  const closeSubPanel = (): void => {
    if (formulaHasChanges && !window.confirm('You have an unsaved formula. Are you sure you want to discard it?'))
      return
    setFormulaHasChanges(false)
    setSubPanel(null)
    setSubPanelFullScreen(false)
  }

  const handleClose = (): void => {
    flushPendingSaves()
    rename.commit(rename.draftName, { onError: handleUpdateColumnError })
  }

  const baseWidth = width ?? EDIT_COLUMN_MENU_WIDTH

  // The hand-entered options: the same editor the add-column configure stage uses, plus the two controls
  // that only make sense once the column exists.
  const manualOptionsPane = (
    <>
      <ColumnManualOptions
        options={manualOptions}
        onAdd={handleAddManualOption}
        onUpdate={handleUpdateManualOption}
        onRemove={handleRemoveManualOption}
        onMove={handleMoveManualOption}
        onSort={handleSortManualOptions}
        suggestions={unusedColumnValues}
        onAddMany={handleAddColumnValues}
        expandOptionsViaCellEntry={currentKindOptions.expandOptionsViaCellEntry}
        onExpandOptionsChange={viewColumnId ? undefined : handleSetExpandOptionsViaCellEntry}
      />
    </>
  )

  // Options taken from a column in another table: the source pickers, the toggles that govern how the
  // values are kept in step, the dependency columns that narrow them, and the resulting read-only list.
  const tableOptionsPane = (
    <div className="pt-4">
      <SelectSourceFields
        tableId={tableId}
        viewId={viewId}
        columnId={viewColumnId}
        onTableChange={handleOnTableChange}
        onViewChange={handleOnTableViewChange}
        onColumnChange={handleOnTableViewColumnChange}
        disabled={disabled}
        onViewLoaded={setSelectedSourceView}
        onRestrictedChange={setSourceRestricted}
      />

      {tableId && viewId && viewColumnId && !sourceRestricted && (
        <div className="px-2.5 mt-2">
          <Link
            to={generatePath(TABLE, { projectId: project.publicId, tableId: tableId, viewId: viewId })}
            target="_blank"
            rel="noopener noreferrer"
          >
            Click to View Selected Table View
          </Link>
        </div>
      )}

      {sourceRestricted && (
        <p className="mx-2.5 mt-4 text-red whitespace-normal">
          You do not have access to the table or view that has been selected by another admin of this table.
          {tableId && viewId && (
            <span>
              {' '}
              <Link
                to={generatePath(TABLE, { projectId: project.publicId, tableId: tableId, viewId: viewId })}
                target="_blank"
                rel="noopener noreferrer"
              >
                Go to the table
              </Link>{' '}
              to request access.
            </span>
          )}
        </p>
      )}

      <div className="mt-4 px-2.5">
        <div className="flex items-center gap-1">
          <Checkbox
            checked={currentKindOptions.tableOptions.liveValues}
            onChange={(event) => handleSetLiveValues(event.target.checked)}
          >
            Keep Values Up To Date
          </Checkbox>
          <InfoHint content={LIVE_VALUES_HELP} />
        </div>
        <div className="flex items-center gap-1">
          <Checkbox
            checked={currentKindOptions.autopopulate}
            onChange={(event) => handleSetAutopopulateValues(event.target.checked)}
          >
            Auto-Populate New Row Values
          </Checkbox>
          <InfoHint content={AUTOPOPULATE_HELP} />
        </div>
      </div>

      {viewColumnId && !sourceRestricted && (
        <div className="mt-4">
          <div className="flex items-center bg-surface-sunken py-2 px-2.5">
            <span className="font-bold text-sm">Dependency Columns</span>
            {!showDependencyColumn && (
              <Button
                internalType="accept"
                className="h-auto ml-auto py-1 px-3 text-xs"
                onClick={() => setShowDependencyColumn(true)}
              >
                Add
              </Button>
            )}
          </div>

          {showDependencyColumn && (
            <>
              {dependencies.map((dependency, index) => {
                const foundCurrentColumn = spreadsheetData.viewDetails.columns.find(
                  (col) => col.publicId === dependency.columnId
                )
                const columnJoinId = dependency.columnJoinId
                  ? dependency.columnJoinId
                  : foundCurrentColumn?.kindOptions?.tableOptions?.columnId
                const columnMatchName = selectedTableViewColumns?.find((col) => col.publicId === columnJoinId)?.name

                return (
                  <div
                    key={`column-select-${index}`}
                    className="flex items-start gap-2 mx-2.5 mt-2 p-2 border border-divider rounded-lg bg-surface-muted text-sm whitespace-normal"
                  >
                    {selectedViewName && columnMatchName && foundCurrentColumn?.name ? (
                      <>
                        <span>
                          {spreadsheetData.tableDetails.name}/{spreadsheetData.viewDetails.name}/
                          {foundCurrentColumn?.name} match to {selectedTableName}/{selectedViewName}/{columnMatchName}
                        </span>
                        <span
                          className="ml-auto shrink-0 text-red cursor-pointer"
                          onClick={() => handleRemoveTableDependency(index)}
                        >
                          <Delete className="h-5 w-5" />
                        </span>
                      </>
                    ) : (
                      <Spinner className="h-4 w-4" />
                    )}
                  </div>
                )
              })}

              <Field
                label={`${spreadsheetData.tableDetails.name}/${spreadsheetData.viewDetails.name}`}
                htmlFor="select-dependency-column"
              >
                <Select
                  inputId="select-dependency-column"
                  options={spreadsheetData.viewDetails.columns.map((column) => {
                    return { label: column.name, value: column.publicId }
                  })}
                  onOptionClick={(option) => handleOnColumnDependencyChange(option)}
                  optionsSelected={dependencyColumn.columnId ? [dependencyColumn.columnId] : []}
                />
              </Field>

              {showDependencyJoinColumn && (
                <Field label={`Match to ${selectedTableName}/${selectedViewName}`} htmlFor="select-dependency-join">
                  <Select
                    inputId="select-dependency-join"
                    options={
                      selectedTableViewColumns
                        ? selectedTableViewColumns.map((column) => {
                            return { label: column.name, value: column.publicId }
                          })
                        : []
                    }
                    onOptionClick={(option) => handleOnColumnDependencyJoinChange(option)}
                    optionsSelected={dependencyColumn.columnJoinId ? [dependencyColumn.columnJoinId] : []}
                    loading={!!viewId && selectedTableViewColumns === undefined}
                  />
                </Field>
              )}

              {canAddDependency && (
                <p className="mx-2.5 mt-2 text-sm text-muted whitespace-normal">
                  {
                    spreadsheetData.viewDetails.columns.find((column) => column.publicId === dependencyColumn?.columnId)
                      ?.name
                  }{' '}
                  {dependencyColumn?.columnJoinId ? (
                    <span>
                      needs to be linked to a column in {selectedTableName}/{selectedViewName}
                    </span>
                  ) : (
                    <span>
                      has already a link to {selectedTableName}/{selectedViewName}.
                    </span>
                  )}
                </p>
              )}

              <div className="px-2.5 mt-2">
                <Button
                  internalType="accept"
                  className="h-auto py-2 px-3"
                  disabled={!canAddDependency}
                  onClick={handleAddDependencyColumn}
                >
                  Add
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {!sourceRestricted && (
        <div className="px-2.5 pt-4">
          <div className="text-sm text-muted mb-2">
            {sourcedOptions.length} sourced option{sourcedOptions.length === 1 ? '' : 's'}
          </div>
          {sourcedOptions.length === 0 ? (
            <div className="text-muted">No options available</div>
          ) : (
            <div>
              {sourcedOptions.map((option, index) => (
                <div
                  key={`${index}-${option}`}
                  className="flex items-center gap-2 border border-divider rounded-lg bg-light-green px-2 py-[7px] mb-1.5"
                >
                  <span className="truncate font-semibold">{option}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tableId && (
        <div className="px-2.5 mt-4">
          <Button
            id="reset-options"
            internalType="danger"
            className="h-auto w-full py-2"
            onClick={handleRemoveTableOptions}
          >
            Reset Source Column Options
          </Button>
          <div className="text-sm text-muted mt-1.5">
            Clears the table, view and column, and empties the sourced list.
          </div>
        </div>
      )}
    </div>
  )

  const settingsColumn = (
    <>
      <Field label="Name" htmlFor="column-name" className="px-2.5 pt-4">
        <Input
          id="column-name"
          type="text"
          value={rename.draftName}
          readOnly={isSynced}
          onChange={(event) => rename.setDraftName(event.target.value)}
          onBlur={(event) => rename.commit(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return
            event.preventDefault()
            rename.commit(event.currentTarget.value)
          }}
        />
        {rename.error ? (
          <div data-testid="column-name-error" className="mt-[5px] text-red whitespace-normal">
            {rename.error}
          </div>
        ) : null}
      </Field>

      <ColumnDescriptionField
        key={publicId}
        description={description}
        editorId={publicId ? publicId : ''}
        tablePublicId={spreadsheetData.tableDetails.publicId}
        onChange={handleDescriptionChange}
      />

      <Field label="Type" htmlFor="column-type">
        <div className="w-full block" data-tour="column-type-selector">
          <Select
            inputId="column-type"
            options={ALL_TYPES.sort((typeA, typeB) =>
              COLUMN_TYPE_NAMES[typeA] > COLUMN_TYPE_NAMES[typeB]
                ? 1
                : COLUMN_TYPE_NAMES[typeA] < COLUMN_TYPE_NAMES[typeB]
                  ? -1
                  : 0
            ).map((type) => {
              return { label: COLUMN_TYPE_NAMES[type], value: type as string, icon: <IconSelector kind={type} /> }
            })}
            disabled={disabled || isSynced}
            optionsSelected={column ? [column.kind] : []}
            onOptionClick={(option) => handleOnKindChange(option)}
          />
          {column && DATE_COLUMN_TYPES.includes(column.kind) && (
            <div className="italic py-1 whitespace-normal">
              The date displayed will be in the user&apos;s local timezone
            </div>
          )}
        </div>
      </Field>

      {kind && <ColumnTypeFormattingFields kind={kind} value={configValue} onChange={handleConfigChange} />}

      {!isJoined &&
        !scriptEnabled &&
        !formulaEnabled &&
        !isSynced &&
        kind &&
        !DEFAULT_VALUE_UNSUPPORTED_KINDS.includes(kind) && (
          <div data-tour="column-default-value">
            <Field
              label={
                <span className="inline-flex items-center gap-1">
                  Default Value
                  {defaultValueError && (
                    <Tooltip content={`${defaultValueError}. It won't be applied to new rows.`}>
                      <span
                        className="inline-flex"
                        data-testid="default-value-error"
                        aria-label={`${defaultValueError}. It won't be applied to new rows.`}
                      >
                        <ErrorOutline className="h-5 w-5 text-red" />
                      </span>
                    </Tooltip>
                  )}
                </span>
              }
              htmlFor="column-default-value"
            >
              {kind === 'select' ? (
                <Select
                  inputId="column-default-value"
                  options={(() => {
                    const opts = getOptions()
                    if (selectDefault && !opts.includes(selectDefault)) opts.push(selectDefault)
                    return opts.map((opt) => ({ label: opt, value: opt }))
                  })()}
                  optionsSelected={selectDefault ? [selectDefault] : []}
                  onOptionClick={(option) => handleSelectDefaultValueChange(option)}
                  clearable
                  placeholder="No default"
                />
              ) : kind === 'multiselect' ? (
                <Select
                  inputId="column-default-value"
                  options={(() => {
                    const opts = getOptions()
                    const extra = multiselectDefaults.filter((v) => !opts.includes(v))
                    return [...opts, ...extra].map((opt) => ({ label: opt, value: opt }))
                  })()}
                  multiselect
                  optionsSelected={multiselectDefaults}
                  onOptionClick={(option) => handleMultiselectDefaultValueChange(option)}
                  placeholder="No default"
                />
              ) : DATE_COLUMN_TYPES.includes(kind) ? (
                <Input
                  id="column-default-value"
                  type={kind === 'datetime' ? 'datetime-local' : 'date'}
                  defaultValue={formatDefaultDateForInput(column?.defaultValue, kind)}
                  onChange={(event) => handleDefaultValueChange(toUTCString(event.target.value, kind) || null)}
                />
              ) : kind === 'checkbox' ? (
                <Select
                  inputId="column-default-value"
                  options={[
                    { label: 'True', value: 'true' },
                    { label: 'False', value: 'false' }
                  ]}
                  optionsSelected={checkboxDefault ? [checkboxDefault] : []}
                  onOptionClick={(option) => handleCheckboxDefaultValueChange(option)}
                  clearable
                  placeholder="No default"
                />
              ) : NUMBER_COLUMN_TYPES.includes(kind) ? (
                <Input
                  id="column-default-value"
                  type="number"
                  defaultValue={column?.defaultValue ?? ''}
                  onChange={(event) => handleDefaultValueChange(event.target.value || null)}
                />
              ) : (
                <Input
                  id="column-default-value"
                  type="text"
                  placeholder={kind === 'json' ? '{ "key": "value" }' : undefined}
                  defaultValue={column?.defaultValue ?? ''}
                  onChange={(event) => handleDefaultValueChange(event.target.value || null)}
                />
              )}
            </Field>
          </div>
        )}

      {isJoined && (
        <div className="mx-2.5 mt-4 p-3 bg-surface-muted rounded border whitespace-normal">
          <div className="block text-sm font-bold mb-2 flex items-center gap-1">
            <Join /> Lookup Source
          </div>

          {columnJoinLoading ? (
            <Spinner className="h-4 w-4" />
          ) : columnJoin ? (
            <div className="text-sm">
              <div>
                {columnJoin.joinTableName} / {columnJoin.joinViewName}
              </div>
              {sourceColumnName && <div className="mt-1 text-muted">Source column: {sourceColumnName}</div>}
              {columnJoin.joinColumns.length > 0 && (
                <div className="mt-1 text-muted">
                  Matching on:{' '}
                  {columnJoin.joinColumns
                    .map(
                      (jc) =>
                        spreadsheetData.tableDetails.columns.find((c) => c.publicId === jc.targetColumnId)?.name ??
                        jc.targetColumnId
                    )
                    .join(', ')}
                </div>
              )}
              <Link
                to={generatePath(TABLE, {
                  projectId: project.publicId,
                  tableId: columnJoin.joinTableId,
                  viewId: columnJoin.joinViewId
                })}
                target="_blank"
                rel="noopener noreferrer"
                className="block mt-1"
              >
                View Source
              </Link>
            </div>
          ) : (
            <div className="text-sm text-muted">Lookup source not found</div>
          )}
        </div>
      )}

      {showSelectOptions && (
        <SegmentedControl
          title="Select Options"
          ariaLabel="Where the options come from"
          options={SELECT_OPTIONS_MODES}
          value={optionsMode}
          onChange={setOptionsMode}
        >
          {optionsMode === 'manual' ? manualOptionsPane : tableOptionsPane}
        </SegmentedControl>
      )}

      <CollapseGroup allowOne>
        <div className="mt-4">
          <Collapse text="Validation" headerClassName="py-2.5 px-2.5" bodyClassName="pt-1 pb-4">
            {kind && <ColumnValidationFields kind={kind} value={configValue} onChange={handleConfigChange} />}
            {regexError ? <div className="px-2.5 mt-[5px] text-red whitespace-normal">{regexError}</div> : null}
          </Collapse>

          <div data-tour="column-formatting-section">
            <Collapse text="Formatting" headerClassName="py-2.5 px-2.5" bodyClassName="pt-1 pb-4">
              <ColumnFormattingFields value={configValue} onChange={handleConfigChange} />
            </Collapse>
          </div>
        </div>
      </CollapseGroup>

      {!isReadOnly && (
        <SubPanelRow
          title="Formula"
          state={formulaEnabled ? 'On' : 'Off'}
          active={subPanel === 'formula'}
          onOpen={() => setSubPanel('formula')}
        />
      )}

      {scriptsAllowed && (
        <SubPanelRow
          title="Script"
          state={scriptEnabled ? 'On' : 'Off'}
          active={subPanel === 'script'}
          onOpen={() => setSubPanel('script')}
        />
      )}
    </>
  )

  return (
    <FloatingMenu
      id={id}
      menuState={menuState}
      setMenuState={setMenuState}
      width={subPanel ? SUB_PANEL_WIDTH : baseWidth}
      fullScreen={subPanel !== null && subPanelFullScreen}
      zIndex={3000}
      shouldBlockClose={formulaHasChanges}
      confirmMessage="You have an unsaved formula. Are you sure you want to close and discard your changes?"
      // Closing (its ×, Esc) unmounts the Name field before it can blur — so closing has to commit
      // the pending rename here or it would be lost, along with any setting still waiting out its
      // debounce.
      onClose={handleClose}
      title="Edit column"
    >
      <div className={panel({ view: panelView(subPanel, subPanelFullScreen) })}>
        {!subPanel && (
          <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 whitespace-normal">{settingsColumn}</div>
        )}

        {subPanel && (
          <div className="flex flex-col min-h-0 flex-1 min-w-0">
            <div className="flex items-center gap-2.5 p-2.5 border-b border-divider shrink-0">
              <Button
                internalType="transparent"
                type="button"
                aria-label="Back to column settings"
                className="h-auto flex items-center text-muted hover:text-body rounded p-0.5 -rotate-270 focus-visible:outline-2 focus-visible:outline-blue"
                onClick={closeSubPanel}
              >
                <ChevronDown />
              </Button>
              <span className="font-bold">{subPanel === 'formula' ? 'Formula' : 'Script'}</span>
              <div className="ml-auto flex items-center gap-2">
                {subPanel === 'script' && (
                  <Button
                    className="w-[100px]"
                    internalType={scriptEnabled ? 'danger' : 'accept'}
                    disabled={isSynced}
                    onClick={() => {
                      handleScriptEnabledChange(!scriptEnabled)
                      if (scriptEnabled) setSubPanel(null)
                    }}
                  >
                    {scriptEnabled ? 'Disable' : 'Enable'}
                  </Button>
                )}
                <Tooltip content={subPanelFullScreen ? 'Exit Full Screen' : 'View Full Screen'} className="w-max">
                  <Button
                    internalType="transparent"
                    type="button"
                    aria-pressed={subPanelFullScreen}
                    aria-label={subPanelFullScreen ? 'Exit Full Screen' : 'View Full Screen'}
                    className="h-auto flex items-center text-muted hover:text-body rounded p-0.5 focus-visible:outline-2 focus-visible:outline-blue"
                    onClick={() => setSubPanelFullScreen((full) => !full)}
                  >
                    {subPanelFullScreen ? <Cross /> : <Expand />}
                  </Button>
                </Tooltip>
              </div>
            </div>

            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
              {subPanel === 'formula' && column && (
                <Formula
                  columnPublicId={column.publicId}
                  formula={formula ? formula : DEFAULT_FORMULA}
                  setEditMenu={setMenuState}
                  setHasChanges={setFormulaHasChanges}
                />
              )}

              {subPanel === 'script' && column && scriptEnabled && (
                <div className="flex-1 min-h-0 overflow-y-auto p-2.5">
                  <Script
                    columnPublicId={column.publicId}
                    script={script}
                    handleUpdateColumnError={handleUpdateColumnError}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {changeColumnKindModalState && selectedKind && (
        <ColumnKindModal
          id="change-column-kind-modal"
          open={changeColumnKindModalState}
          setOpen={setChangeColumnKindModalState}
          newKind={selectedKind}
          columnId={publicId}
          onCancel={() => {
            if (column) {
              setSelectedKind(column.kind)
            }
          }}
          zIndex={3001}
        />
      )}
    </FloatingMenu>
  )
}

export default EditColumnMenu
